/**
 * useKeyboardNavigation Hook
 * Handles keyboard interactions for accessibility
 */
import { useCallback, useEffect } from 'react';
import { CalendarMode } from '../../../contracts/YearlyCalendarContracts';

export interface UseKeyboardNavigationOptions {
  enabled?: boolean;
  viewMode: CalendarMode;
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void;
  onSelect: () => void;
  onEscape?: () => void;
  onToday?: () => void;
  containerRef: React.RefObject<HTMLElement>;
}

export interface UseKeyboardNavigationReturn {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Key codes for navigation
 */
const KEYS = {
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  HOME: 'Home',
  END: 'End',
  PAGE_UP: 'PageUp',
  PAGE_DOWN: 'PageDown',
  T: 't',
  T_UPPER: 'T',
};

/**
 * Custom hook for keyboard navigation
 */
export const useKeyboardNavigation = (
  options: UseKeyboardNavigationOptions
): UseKeyboardNavigationReturn => {
  const {
    enabled = true,
    viewMode,
    onNavigate,
    onSelect,
    onEscape,
    onToday,
    containerRef,
  } = options;
  
  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!enabled) return;

    // The listener is attached at the calendar container level, so only respond to
    // keys that originate from a focusable grid cell. Otherwise Enter/Space bubbling
    // up from navigation buttons, legend controls or popup rows would be hijacked and
    // trigger day selection instead of the control's own activation (ND1). Escape is
    // still handled by the popup's own document-level listener.
    const target = e.target as HTMLElement | null;
    if (target && !target.closest('[role="gridcell"]')) {
      return;
    }

    switch (e.key) {
      case KEYS.ARROW_UP:
        e.preventDefault();
        onNavigate('up');
        break;
        
      case KEYS.ARROW_DOWN:
        e.preventDefault();
        onNavigate('down');
        break;
        
      case KEYS.ARROW_LEFT:
        e.preventDefault();
        onNavigate('left');
        break;
        
      case KEYS.ARROW_RIGHT:
        e.preventDefault();
        onNavigate('right');
        break;
        
      case KEYS.ENTER:
      case KEYS.SPACE:
        e.preventDefault();
        onSelect();
        break;
        
      case KEYS.ESCAPE:
        if (onEscape) {
          e.preventDefault();
          onEscape();
        }
        break;
        
      case KEYS.T:
      case KEYS.T_UPPER:
        if (onToday) {
          e.preventDefault();
          onToday();
        }
        break;
        
      case KEYS.HOME:
        // Navigate to start of week/month/year
        e.preventDefault();
        // Multiple left navigations to reach start
        for (let i = 0; i < 7; i++) {
          onNavigate('left');
        }
        break;
        
      case KEYS.END:
        // Navigate to end of week/month/year
        e.preventDefault();
        // Multiple right navigations to reach end
        for (let i = 0; i < 7; i++) {
          onNavigate('right');
        }
        break;
        
      case KEYS.PAGE_UP:
        // Navigate to previous month/year
        e.preventDefault();
        // Multiple up navigations
        for (let i = 0; i < 4; i++) {
          onNavigate('up');
        }
        break;
        
      case KEYS.PAGE_DOWN:
        // Navigate to next month/year
        e.preventDefault();
        // Multiple down navigations
        for (let i = 0; i < 4; i++) {
          onNavigate('down');
        }
        break;
        
      default:
        // Let other keys pass through
        break;
    }
  }, [enabled, onNavigate, onSelect, onEscape, onToday]);
  
  // Set up global keyboard listener when container is focused
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;
    
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only handle if the container or a descendant is focused
      if (!container.contains(document.activeElement)) return;
      
      // T key shortcut for today
      if ((e.key === 't' || e.key === 'T') && onToday) {
        e.preventDefault();
        onToday();
      }
    };
    
    document.addEventListener('keydown', handleGlobalKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [enabled, containerRef, onToday]);
  
  return {
    handleKeyDown,
  };
};

export default useKeyboardNavigation;
